import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from 'react';
import {
  IDoesFilterPassParams,
  IFilterParams,
  IFilterComp,
} from 'ag-grid-community';
import DurationFilterComponent, {
  DurationFilterValue,
  FilterType,
} from './DurationFilterComponent/DurationFilterComponent';

// ag-Grid DurationFilterModel interface
interface DurationFilterModel {
  filterType: 'number';
  type: FilterType;
  filter: number;
  filterTo?: number;
}

// Type definitions for ag-Grid internal APIs
interface IMenuFactory {
  hideActiveMenu?: () => void;
}

interface IPopupService {
  hidePopup?: () => void;
}

interface IContext {
  getBean?: (name: string) => IPopupService | undefined;
}

interface IGridApiInternals {
  menuFactory?: IMenuFactory;
  context?: IContext;
}

type GridApiWithInternals = Partial<IGridApiInternals> &
  Record<string, unknown>;

const getGridApiInternals = (api: unknown) => api as GridApiWithInternals;

const AgGridDurationFilter = forwardRef<IFilterComp, IFilterParams>(
  (props, ref) => {
    const [filterValue, setFilterValue] = useState<DurationFilterValue | null>(
      null
    );
    const [filterChangeCounter, setFilterChangeCounter] = useState<number>(0);
    const guiRef = useRef<HTMLDivElement>(null);

    // Effect to trigger filter change after filterValue state is updated
    useEffect(() => {
      if (filterChangeCounter > 0) {
        props.filterChangedCallback?.();
        props.api?.onFilterChanged();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterValue, filterChangeCounter]);

    useImperativeHandle(ref, () => ({
      // Required: Returns the DOM element for ag-Grid to render
      getGui(): any {
        return guiRef.current;
      },

      // Required: Tells ag-Grid if filter is currently active
      isFilterActive(): boolean {
        return filterValue != null;
      },

      // Required: THE CORE FILTERING LOGIC - filters each row
      // NOTE: Backend handles all filtering - we don't filter on the frontend
      // This method is required by ag-Grid interface but always returns true
      doesFilterPass(params: IDoesFilterPassParams): boolean {
        // Always return true - backend already filtered the data
        return true;
      },

      // Required: Returns current filter state (for persistence)
      getModel(): DurationFilterModel | null {
        if (!filterValue) {
          return null;
        }

        const model: DurationFilterModel = {
          filterType: 'number',
          type: filterValue.type,
          filter: filterValue.fromMinutes,
        };

        const isInRangeWithToMinutes =
          filterValue.type === 'inRange' && filterValue.toMinutes !== undefined;

        if (isInRangeWithToMinutes) {
          model.filterTo = filterValue.toMinutes;
        }

        return model;
      },

      // Required: Restores filter state (from persistence)
      setModel(model: DurationFilterModel | null): void {
        const isModelInvalid = !model || model.filter === undefined;

        if (isModelInvalid) {
          setFilterValue(null);
          return;
        }

        const value: DurationFilterValue = {
          type: model.type,
          fromMinutes: model.filter,
        };

        const isInRangeWithFilterTo =
          model.type === 'inRange' && model.filterTo !== undefined;

        if (isInRangeWithFilterTo) {
          value.toMinutes = model.filterTo;
        }

        setFilterValue(value);
      },
    }));

    const handleApply = (value: DurationFilterValue | null) => {
      setFilterValue(value);
      setFilterChangeCounter((prev) => prev + 1);
    };

    const handleReset = () => {
      setFilterValue(null);
      setFilterChangeCounter((prev) => prev + 1);
    };

    const handleClose = () => {
      props.filterModifiedCallback?.();

      const isApiMissing = !props.api;

      if (isApiMissing) {
        return;
      }

      const gridApi = getGridApiInternals(props.api);

      if (!!gridApi.menuFactory?.hideActiveMenu) {
        gridApi.menuFactory.hideActiveMenu();
        return;
      }

      if (!!gridApi.context?.getBean) {
        try {
          const popupService = gridApi.context.getBean('popupService');

          if (!!popupService?.hidePopup) {
            popupService?.hidePopup();
            return;
          }
        } catch (e) {
          // Ignore - popupService might not be available
        }
      }

      // Fallback: Simulate ESC key press
      document.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          code: 'Escape',
          keyCode: 27,
          which: 27,
          bubbles: true,
          cancelable: true,
        })
      );
    };

    return (
      <div ref={guiRef} className="ag-custom-filter-container">
        <DurationFilterComponent
          onApply={handleApply}
          onReset={handleReset}
          onClose={handleClose}
          currentFilter={filterValue}
        />
      </div>
    );
  }
);

AgGridDurationFilter.displayName = 'AgGridDurationFilter';

export default AgGridDurationFilter;
